var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { hasValue, isOfflineDeployment } from "@utils";
import { UXPContext } from "@core/context/Context";
import axios from "axios";
const AddOnS3BaseUrl = "https://s3-ap-southeast-1.amazonaws.com/marketplace.iviva/use-cases";
const LambdaBaseUrl = "https://lmg8umexs2.execute-api.ap-southeast-1.amazonaws.com/marketplace";
const AddOnContext = createContext(null);
/**
 * @export
 * A provider component that manages add-on data and provides utility functions via context.
 *
 * @example
 * ```
 * <AddOnProvider>
 *   <MyComponent />
 * </AddOnProvider>
 * ```
 *
 * @example
 * ```
 * <AddOnProvider>
 *   <div>
 *     <MyComponent />
 *     <AnotherComponent />
 *   </div>
 * </AddOnProvider>
 * ```
 */
export const AddOnProvider = ({ children }) => {
    const context = useContext(UXPContext);
    const env = (context === null || context === void 0 ? void 0 : context.environment) === "dev" ? "dev" : "prod";
    const [addOns, setAddOns] = useState([]);
    const [installedAddOns, setInstalledAddOns] = useState([]);
    const addOnsRef = useRef([]);
    const installedAddOnsRef = useRef([]);
    useEffect(() => {
        getAllAddOns();
        getAllInstalledAddOns();
    }, []);
    const getAllAddOns = () => {
        // the add-on catalogue is an internet service (marketplace lambda) -
        // never call out from air-gapped deployments
        if (env === "dev" || isOfflineDeployment()) {
            setAddOns([]);
            return;
        }
        if (addOnsRef.current.length > 0) {
            setAddOns(addOnsRef.current);
            return;
        }
        const url = `${LambdaBaseUrl}/use-cases?from=0&size=10000`;
        axios
            .get(url)
            .then((res) => {
            var _a, _b;
            let _addOns = (((_b = (_a = res.data) === null || _a === void 0 ? void 0 : _a.hits) === null || _b === void 0 ? void 0 : _b.hits) || []).map((r) => r._source);
            setAddOns(_addOns);
            addOnsRef.current = _addOns;
        })
            .catch((e) => {
            console.log("Unable to get the addons. Exception: ", e);
            setAddOns([]);
            addOnsRef.current = [];
        });
    };
    const getAllInstalledAddOns = () => __awaiter(void 0, void 0, void 0, function* () {
        return new Promise((done) => __awaiter(void 0, void 0, void 0, function* () {
            try {
                if (env === "dev") {
                    setInstalledAddOns([]);
                    done();
                    return;
                }
                const res = yield context.executeService("Lucy", "InstalledUsecase:All", {}, { json: true, executeImmidiately: true, key: "get-all-installed-add-ons" });
                setInstalledAddOns(res);
                installedAddOnsRef.current = res;
                done();
            }
            catch (e) {
                console.log("Unable to get the installed add-ons. Exception: ", e);
                setInstalledAddOns([]);
                done();
            }
        }));
    });
    const isAddOn = (moduleId) => {
        if (env === "dev")
            return false;
        return addOns.some((a) => a.id === moduleId);
    };
    const getAddOnDetails = (moduleId) => {
        return isAddOn(moduleId) ? addOns.find((a) => a.id === moduleId) : null;
    };
    const isConfigurationRequired = (moduleId) => {
        var _a;
        const details = getAddOnDetails(moduleId);
        return ((_a = details === null || details === void 0 ? void 0 : details.configUI) === null || _a === void 0 ? void 0 : _a.default) !== undefined;
    };
    const isAddOnInstalledAndConfigured = (moduleId) => {
        var _a, _b, _c;
        const instance = installedAddOnsRef.current.find((ia) => ia.Usecase === moduleId);
        // The install record alone is not enough: its module (bundle) can be deleted on its own,
        // leaving nothing to load. Installed means the module is on the account too.
        const hasModule = ((_c = (_b = (_a = context === null || context === void 0 ? void 0 : context.scriptFiles) === null || _a === void 0 ? void 0 : _a[moduleId]) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0) > 0;
        return instance && hasModule
            ? { installed: true, configured: instance.HasConfigured === "1" }
            : { installed: false, configured: false };
    };
    const getThunmbNailImagePath = (moduleId) => {
        let details = getAddOnDetails(moduleId);
        return details ? `${AddOnS3BaseUrl}/${moduleId}/latest/${details.thumbnail}` : null;
    };
    const getAddOnName = (widget, moduleId) => {
        var _a;
        if ((!widget && !moduleId) || !hasValue(moduleId))
            return null;
        if (widget && hasValue(widget.usecaseName))
            return widget.usecaseName;
        if (hasValue(moduleId)) {
            const details = getAddOnDetails(moduleId);
            return (_a = details === null || details === void 0 ? void 0 : details.title) !== null && _a !== void 0 ? _a : null;
        }
        return null;
    };
    return (_jsx(AddOnContext.Provider, { value: {
            AddOnS3BaseUrl,
            addOns,
            installedAddOns,
            isAddOn,
            getAddOnDetails,
            isAddOnInstalledAndConfigured,
            getThunmbNailImagePath,
            isConfigurationRequired,
            refreshInstalledAddOns: getAllInstalledAddOns,
            getAddOnName,
        }, children: children }));
};
/**
 * @export
 * A custom React hook that retrieves the AddOnContext, throwing an error if used outside an AddOnProvider.
 *
 * @returns The AddOnContext object containing add-on data and utility functions.
 * @throws Error if the hook is used outside an AddOnProvider.
 *
 * @example
 * ```
 * const MyComponent = () => {
 *   const { addOns, isAddOn } = useAddOns();
 *   return (
 *     <div>
 *       {addOns.map(addOn => (
 *         <p key={addOn.id}>{addOn.title}: {isAddOn(addOn.id) ? 'Add-On' : 'Not an Add-On'}</p>
 *       ))}
 *     </div>
 *   );
 * };
 *
 * ```
 *
 * @example
 * ```
 * const MyComponent = () => {
 *   const { getAddOnDetails, getThunmbNailImagePath } = useAddOns();
 *   const addOn = getAddOnDetails('module-1');
 *   return (
 *     <div>
 *       {addOn && (
 *         <div>
 *           <h2>{addOn.title}</h2>
 *           <img src={getThunmbNailImagePath('module-1')} alt={addOn.title} />
 *         </div>
 *       )}
 *     </div>
 *   );
 * };
 * ```
 */
export const useAddOns = () => {
    const context = useContext(AddOnContext);
    if (!context) {
        throw new Error("useAddOns must be used within an AddOnProvider");
    }
    return context;
};
